import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ApiError, explain } from '@/shared/api/problem'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { listUnits, getUnit, EMPTY_UNIT_FILTERS } from '@/features/geography/api'
import type { UnitResponse } from '@/shared/api/types'

/**
 * A place, filtered to the LEVEL a seat is contested at.
 *
 * ## Why a flat search and not the cascade the incident wizard uses
 *
 * That one walks county → constituency → ward because an incident can happen at any level
 * and the reporter is orienting themselves. Here the level is already decided by the seat,
 * and the user knows the name — "Embakasi East" — so making them pick Nairobi first is three
 * clicks to reach a thing they could have typed. One search, scoped server-side to the right
 * `type`.
 *
 * ## The level is a filter, not a rule
 *
 * Passing `level` narrows the query so the list only contains valid choices. It does NOT
 * decide validity: the server's `SeatTypeRules` does, and it refuses a mismatch with a
 * problem naming the level it wanted. This just makes the right answer the easy one.
 */
export function UnitPicker({
  level, value, onPick, disabled,
}: {
  /** COUNTRY | COUNTY | CONSTITUENCY | WARD, from the chosen seat. */
  level: string | null
  value: string | null
  onPick: (unitId: string | null) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debounced = useDebounced(term, 300)
  const [rows, setRows] = useState<UnitResponse[]>([])
  const [chosen, setChosen] = useState<UnitResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  // Resolve an existing value to its name, so an amend form does not show a uuid.
  // `value` is listed: reopening on another row must re-resolve.
  useEffect(() => {
    if (!value) { setChosen(null); return }
    if (chosen?.id === value) return
    let alive = true
    void getUnit(value)
      .then((u) => { if (alive) setChosen(u as unknown as UnitResponse) })
      .catch(() => { if (alive) setChosen(null) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const page = await listUnits({
        ...EMPTY_UNIT_FILTERS,
        q: debounced,
        // A null level means "no seat chosen yet" — do not silently list every unit in the
        // country, which is 46,000 rows and none of them necessarily right.
        type: level ? [level] : [],
        sort: 'name',
      }, 0, 30)
      setRows(page.content)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setDenied(true)
      else setError(explain(err))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [debounced, level])

  useEffect(() => { if (open && level) void run() }, [open, level, run])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (denied) {
    return (
      <p className="text-erp-xs text-muted-foreground border-l-2 border-amber-400 pl-2 py-1">
        The unit list needs <code className="font-mono">geography:read</code>, which this
        account does not hold. Ask an administrator for it.
      </p>
    )
  }

  return (
    <div ref={box} className="relative">
      <button type="button" disabled={disabled}
        onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="listbox"
        className="erp-input h-9 w-full inline-flex items-center gap-2 text-left disabled:opacity-60">
        <span className={cn('truncate flex-1 min-w-0', !chosen && 'text-muted-foreground')}>
          {chosen ? chosen.name : (disabled ? 'Choose a seat first' : 'Search places…')}
        </span>
        {chosen && (
          <span role="button" tabIndex={0} aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onPick(null); setChosen(null) }}
            className="shrink-0 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto erp-card shadow-lg">
          <div className="flex items-center gap-2 px-3 h-9 border-b border-border sticky top-0 bg-card">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder={level ? `Search ${level.toLowerCase()}s…` : 'Search…'}
              className="bg-transparent outline-none text-erp-sm w-full min-w-0" />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 opacity-60" />}
          </div>
          {error && <p className="px-3 py-2 text-erp-xs text-red-700 dark:text-red-400">{error}</p>}
          {!loading && rows.length === 0 && !error && (
            <p className="px-3 py-3 text-erp-xs text-muted-foreground">
              {term ? `No ${level?.toLowerCase() ?? 'place'} matches “${term}”.`
                : 'Type to search.'}
            </p>
          )}
          <ul role="listbox">
            {rows.map((u) => (
              <li key={u.id}>
                <button type="button"
                  onClick={() => { onPick(u.id); setChosen(u); setOpen(false); setTerm('') }}
                  className="w-full text-left px-3 py-2 hover:bg-muted/60">
                  <span className="block text-erp-sm truncate">{u.name}</span>
                  <span className="block text-erp-xs text-muted-foreground font-mono">
                    {u.code}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
